/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.0.0-alpha1): form-check-inea.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import { getjQuery } from '../util/index'
import Data from '../dom/data'
import EventHandler from '../dom/event-handler'
import SelectorEngine from '../dom/selector-engine'

/**
 * ------------------------------------------------------------------------
 * Constants
 * ------------------------------------------------------------------------
 */

const NAME = 'form-check-inea'
const VERSION = '5.0.0-alpha1'
const DATA_KEY = 'bs.form-check-inea'
const EVENT_KEY = `.${DATA_KEY}`
const DATA_API_KEY = '.data-api'

const SELECTOR_FORM_CHECK_CONTROLLER = '.form-check-controller .form-check-input'

const EVENT_FORM_CHECK_GROUP_CHANGE = `change${EVENT_KEY}${DATA_API_KEY}`
const EVENT_FORM_CHECK_CHANGE = `change${EVENT_KEY}${DATA_API_KEY}`

/**
 * ------------------------------------------------------------------------
 * Class Definition
 * ------------------------------------------------------------------------
 */

class FormCheckInea {
  constructor(element) {
    this._formCheckGroup = element
    this._formCheckController = SelectorEngine.findOne(SELECTOR_FORM_CHECK_CONTROLLER, this._formCheckGroup)
    this._formChecks = SelectorEngine.find(this.formChecksSelectors(), this._formCheckGroup)
    this.addFormCheckControllerListener()
    this.addFormChecksListener()
    Data.setData(element, DATA_KEY, this)
  }

  // Getters

  static get VERSION() {
    return VERSION
  }

  // Public

  formChecksSelectors() {
    const controlsIds = (this._formCheckController.dataset.controls || '').split(' ')
    const controlsSelector = controlsIds.filter(id => Boolean(id)).map(id => `#fr.gouv.finances.lombok:lombok-archetype-vuejs-jersey:maven-archetype:1.3.0`).join(', ')
    return controlsSelector
  }

  addFormCheckControllerListener() {
    EventHandler.on(this._formCheckController, EVENT_FORM_CHECK_GROUP_CHANGE, event => {
      this._formCheckController.setAttribute('aria-checked', `${event.target.checked}`)
      this._formChecks.forEach(formCheck => {
        formCheck.checked = event.target.checked
        formCheck.setAttribute('aria-checked', `${event.target.checked}`)
      })
    })
  }

  addFormChecksListener() {
    this._formChecks.forEach(formCheck => {
      EventHandler.on(formCheck, EVENT_FORM_CHECK_CHANGE, _ => {
        const allChecked = this._formChecks.every(formCheck => formCheck.checked)
        const allUnchecked = this._formChecks.every(formCheck => !formCheck.checked)
        if (allChecked) {
          this._formCheckController.indeterminate = false
          this._formCheckController.checked = true
          this._formCheckController.setAttribute('aria-checked', 'true')
        } else if (allUnchecked) {
          this._formCheckController.indeterminate = false
          this._formCheckController.checked = false
          this._formCheckController.setAttribute('aria-checked', 'false')
        } else {
          this._formCheckController.indeterminate = true
          this._formCheckController.setAttribute('aria-checked', 'mixed')
        }
      })
    })
  }

  // Static

  static jQueryInterface() {
    return this.each(function () {
      let data = Data.getData(this, DATA_KEY)

      if (!data) {
        data = new FormCheckInea(this)
      }
    })
  }

  static getInstance(element) {
    return Data.getData(element, DATA_KEY)
  }
}

const $ = getjQuery()

/**
 * ------------------------------------------------------------------------
 * jQuery
 * ------------------------------------------------------------------------
 * add .form-check-inea to jQuery only if jQuery is present
 */
/* istanbul ignore if */
if ($) {
  const JQUERY_NO_CONFLICT = $.fn[NAME]
  $.fn[NAME] = FormCheckInea.jQueryInterface
  $.fn[NAME].Constructor = FormCheckInea

  $.fn[NAME].noConflict = () => {
    $.fn[NAME] = JQUERY_NO_CONFLICT
    return FormCheckInea.jQueryInterface
  }
}

export default FormCheckInea
